/* iter-51x / iter55u — Shared "Test connection + alert-email + last-tested
 * stamp" triple for the Admin API-Keys page. Used by the Stripe & Resend
 * cards. Canva has its own card because its alert email lives in a
 * separate `app_config` doc (id="main") behind a different endpoint.
 *
 * Props:
 *   testEndpoint    — POST URL (e.g. "/api/admin/stripe/test-connection")
 *   alertEmail      — current value from cfg (canonical: cfg.{provider}_alert_email)
 *   onAlertEmailChange — (string) => void
 *   onAlertEmailSave   — async () => void   (parent decides what to send)
 *   lastTestAt      — ISO string or ""
 *   lastTestOk      — true | false | null
 *   lastTestMessage — string
 *   configured      — bool; gates the Test button
 *   testIdPrefix    — e.g. "admin-stripe" — used to derive data-testid
 *   onAfterTest     — () => void   (parent reload hook)
 *
 * iter55u — Presentational bits (`AlertEmailRow`, `LastTestStamp`,
 * `ProbeResultPanel`) live in `ProviderProbeParts.jsx`. This file only
 * owns the two handlers (`runTest`, `saveEmail`) and the layout.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { PlugZap, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertEmailRow, LastTestStamp, ProbeResultPanel,
} from './ProviderProbeParts';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function ProviderProbeControls({
  testEndpoint,
  alertEmail,
  onAlertEmailChange,
  onAlertEmailSave,
  lastTestAt,
  lastTestOk,
  lastTestMessage,
  configured,
  testIdPrefix,
  onAfterTest,
}) {
  const [testing, setTesting] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [result, setResult] = useState(null);

  const runTest = async () => {
    if (!configured) {
      toast.error('Configure the key first.');
      return;
    }
    setTesting(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}${testEndpoint}`);
      setResult(r.data);
      if (r.data?.ok) toast.success(r.data.message || 'Probe succeeded.');
      else toast.error(r.data?.message || 'Probe failed.');
      onAfterTest?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Probe request failed.';
      setResult({ ok: false, stage: 'request', status: null, message: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const saveEmail = async () => {
    setSavingEmail(true);
    try {
      await onAlertEmailSave?.();
      toast.success('Alert email saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSavingEmail(false);
    }
  };

  // Prefer the in-session result (has full stage/status), fall back to
  // the persisted stamp from props.
  const stampOk      = result?.ok ?? lastTestOk;
  const stampAt      = result ? new Date().toISOString() : lastTestAt;
  const stampMessage = result?.message || lastTestMessage;

  return (
    <div className="mt-3 space-y-3">
      <AlertEmailRow
        alertEmail={alertEmail}
        onAlertEmailChange={onAlertEmailChange}
        onSave={saveEmail}
        saving={savingEmail}
        testIdPrefix={testIdPrefix}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={runTest}
          disabled={testing || !configured}
          className="h-8 gap-1.5"
          data-testid={`${testIdPrefix}-test-connection`}
          title={configured ? 'Probe the configured key' : 'Configure the key first'}
        >
          {testing
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <PlugZap className="h-3.5 w-3.5" />}
          Test connection
        </Button>
        <LastTestStamp
          ok={stampOk}
          at={stampAt}
          message={stampMessage}
          testIdPrefix={testIdPrefix}
        />
      </div>
      <ProbeResultPanel result={result} testIdPrefix={testIdPrefix} />
    </div>
  );
}
